/**
 * Shared layout for a use-case page.
 * @file src/web/features/usecases/components/UseCaseTemplate.tsx
 *
 * The template holds the layout only. Every word comes from the case data, so
 * these pages differ in substance rather than in swapped nouns.
 */

import { useNavigate } from 'react-router-dom';
import { Button } from '@bloomneo/uikit';
import { ArrowRight, Check, AlertTriangle } from 'lucide-react';
import { SEO } from '@/shared/SEO';
import { StructuredData } from '@/shared/StructuredData';
import { getUseCase, useCases } from '../data/cases';

interface UseCaseTemplateProps {
  slug: string;
}

export const UseCaseTemplate: React.FC<UseCaseTemplateProps> = ({ slug }) => {
  const navigate = useNavigate();
  const useCase = getUseCase(slug);

  if (!useCase) return null;

  const others = useCases.filter((other) => other.slug !== slug).slice(0, 3);
  const canonical = `https://bloomneo.com/usecases/${useCase.slug}`;

  // FAQPage earns the expandable answers in search results; BreadcrumbList
  // replaces the bare URL under the title with a readable trail.
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        mainEntity: useCase.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://bloomneo.com/' },
          { '@type': 'ListItem', position: 2, name: 'Use cases', item: 'https://bloomneo.com/sitemap' },
          { '@type': 'ListItem', position: 3, name: useCase.title, item: canonical },
        ],
      },
      {
        '@type': 'Service',
        serviceType: 'Managed infrastructure',
        provider: { '@type': 'Organization', name: 'Bloomneo', url: 'https://bloomneo.com/' },
        areaServed: 'IN',
        description: useCase.description,
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${useCase.metaTitle} | Bloomneo`}
        description={useCase.description}
        keywords={useCase.keywords}
        canonical={canonical}
      />
      <StructuredData data={structuredData} />
        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="pt-16 pb-12 md:pt-24 md:pb-14">
          <div>
            <div className="bloom-rise">
              <div className="flex items-center gap-3 mb-7">
                <div className="h-px w-8 bg-primary" />
                <span className="text-xs font-medium tracking-[0.2em] uppercase text-primary">
                  {useCase.eyebrow}
                </span>
              </div>
              <h1
                className="font-bold tracking-[-0.03em] leading-[1.02] max-w-3xl"
                style={{ fontSize: 'clamp(2rem, 4.6vw, 3.25rem)' }}
              >
                {useCase.title}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
                {useCase.description}
              </p>
            </div>
          </div>
        </section>

        {/* ── Recognition ──────────────────────────────────────────────────── */}
        <section className="py-14 md:py-20 bg-muted/40 border-y border-border">
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <div className="bloom-rise">
                <h2
                  className="font-bold tracking-[-0.02em] leading-[1.1] mb-4"
                  style={{ fontSize: 'clamp(1.5rem, 3.2vw, 2rem)' }}
                >
                  Does this sound familiar?
                </h2>
                <p className="text-muted-foreground leading-relaxed">{useCase.intro}</p>
              </div>
              <div className="bloom-rise">
                <ul className="flex flex-col gap-3">
                  {useCase.signs.map((sign) => (
                    <li key={sign} className="flex items-start gap-3">
                      <Check className="h-4 w-4 mt-1 shrink-0 text-primary" />
                      <span className="text-sm text-muted-foreground leading-relaxed">{sign}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── What happens ─────────────────────────────────────────────────── */}
        <section className="py-16 md:py-24">
          <div>
            <div className="bloom-rise">
              <h2
                className="font-bold tracking-[-0.02em] leading-[1.05] mb-3"
                style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}
              >
                What we do, in order
              </h2>
              <p className="text-muted-foreground max-w-xl mb-12">
                Roughly the first week. The sequence matters more than the speed.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              {useCase.firstWeek.map((step, index) => (
                <div key={step.title} className="bloom-rise">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-semibold tabular-nums">
                      {index + 1}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-base mb-1.5 tracking-tight">{step.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── The risk ─────────────────────────────────────────────────────── */}
        <section className="pb-16 md:pb-20">
          <div>
            <div className="bloom-rise">
              <div className="rounded-xl border border-border bg-muted/40 p-6 md:p-8 max-w-3xl">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-base mb-2 tracking-tight">{useCase.risk.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{useCase.risk.body}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────────── */}
        <section className="pb-16 md:pb-20">
          <div>
            <div className="bloom-rise">
              <h2
                className="font-bold tracking-[-0.02em] leading-[1.05] mb-8"
                style={{ fontSize: 'clamp(1.5rem, 3.2vw, 2rem)' }}
              >
                Common questions
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-5xl">
              {useCase.faqs.map((faq) => (
                <div key={faq.q} className="bloom-rise">
                  <div>
                    <h3 className="font-semibold text-base mb-2 tracking-tight">{faq.q}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Close ────────────────────────────────────────────────────────── */}
        <section className="pb-16 md:pb-20">
          <div>
            <div className="bloom-rise">
              <div className="bg-primary text-primary-foreground rounded-2xl px-7 py-12 md:px-14 md:py-16">
                <h2
                  className="font-bold tracking-[-0.02em] leading-[1.05] max-w-lg"
                  style={{ fontSize: 'clamp(1.5rem, 3.2vw, 2rem)' }}
                >
                  {useCase.closing}
                </h2>
                <Button
                  size="lg"
                  variant="secondary"
                  className="mt-8 px-7 rounded-full gap-2"
                  onClick={() => navigate('/contact')}
                >
                  Talk to us <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Other situations ─────────────────────────────────────────────── */}
        <section className="pb-20 md:pb-28">
          <div>
            <div className="bloom-rise">
              <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-5">
                Other situations we take on
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {others.map((other) => (
                  <button
                    key={other.slug}
                    onClick={() => navigate(`/usecases/${other.slug}`)}
                    className="text-left rounded-xl border border-border bg-card p-5 hover:border-primary/40 transition-colors group"
                  >
                    <p className="text-[11px] font-medium uppercase tracking-wider text-primary mb-2">
                      {other.eyebrow}
                    </p>
                    <h3 className="font-semibold text-sm mb-1.5 tracking-tight">{other.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{other.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
    </>
  );
};
